import { Injectable } from '@angular/core';
import { ServiceBase, OrigamiResponse } from '@app/origami-api/services/base/service.base';
import { Observable, tap } from 'rxjs';
import { RequiredProvidedLookup, ErrorCode, Processes } from '../constants';
import { Topic, SimpleTopic, TopicField } from '../utils/topic-utils';
import { ErrorRecord } from '../utils/error-utils';
import { Converter } from '@app/shared/utils/converter';
import { DateFormatStore } from '@app/session/date-format-store';
import { HttpXhrBackend } from '@angular/common/http';
import { CASAuthRequestProxy } from './base/request-proxy/cas-auth.request-proxy.class';
import { TimeZoneService } from '@app/services/time-zone.service';
import { FolderManagementServiceModel } from './folder-management.service';
import { TopicServiceOptions } from '@app/origami-api/models/topic-service.model';

export namespace RequiredDocumentServiceOptions {
  export interface GetRequirementsStatus {
    requirementLevel: string;
    siteId: number;
    viewMode: number;
    filter?: number;
  }

  export interface GetRequiredDocuments {
    siteId: number;
    eventTopicId?: number;
    requiredBy: number;
    requiredById: number;
    providedBy: number;
    providedById: number;
    documentTypeId?: number;
    skipFieldsValidation?: boolean;
    start?: number;
    limit?: number;
    orderBy?: string;
    providedByList?: number[];
    requiredByList?: number[];
    requiredByIdList?: number[];
    providedByIdList?: number[];
  }

  export interface GetDefaultRequiredDocumentsForEvent {
    siteId: number;
    eventFilter?: {
      SelectOption?: number;
      EventTypeId?: number;
      EventTopicId?: number;
      Countries?: [];
      InvestigativeSiteIds?: [];
      GetEvents?: [];
    };
  }

  export interface CreateOrEditRequiredDocument {
    siteId: number;
    requiredDocuments: RequiredDocument[];
  }

  export interface RequiredDocument {
    DocumentTypeId: number;
    ProvidedBy: RequiredProvidedLookup;
    ProvidedById: number | string;
    RequiredBy: RequiredProvidedLookup;
    RequiredById?: number | string;
    RequiredDocumentId?: number;
    RequiredDocumentMetadata: RequiredDocumentMetadata[];
    EventTopicId?: number;
    SiteId: number;
  }

  interface RequiredDocumentMetadata {
    // TODO:
    FieldName: string;
    FieldType: number;
    FieldValue: number | string;
  }

  export interface DeleteRequiredDocument {
    checkIfEssentialDocumentsExist: boolean;
    requiredDocuments: {
      RequiredDocumentId: number;
      SiteId: number;
    }[];
    siteId: number;
  }

  export interface GetRequiredDocumentEntities {
    requiredBy: number;
    providedBy?: number;
    eventTypeId?: number;
    siteId: number;
    eventFilter?: {
      SelectOption?: number;
      EventTypeId?: number;
      EventTopicId?: number;
      Countries?: any;
      InvestigativeSiteIds?: any;
      GetEvents?: [];
    };
  }

  export interface GetRequiredDocumentEntitiesResponse {
    EntityId: string;
    EntityName: string;
    ProvidedBy?: RequiredProvidedLookup;
    ProvidedById?: number;
    RequiredBy?: RequiredProvidedLookup;
    RequiredById?: number;
    ReqDocsCount?: number;
  }

  export interface ChangeRequiredDocumentStatus {
    siteId: number;
    topics: {
      SiteId: number;
      TopicFields: TopicField[];
      TopicId: number;
    }[];
    notRequired: boolean;
    changeReason: string;
    mode?: ChangeRequiredDocumentStatusMode;
  }
}

export namespace RequiredDocumentServiceModel {
  export interface RequirementsStatus {
    CollectedCount: number;
    SubmittedCount?: number;
    CollectedDocumentsCount?: number;
    SubmittedDocumentsCount?: number;
    CollectedRecommendedDocumentsCount?: number;
    SubmittedRecommendedDocumentsCount?: number;
    PlaceholdersCount?: number;
    ContactId: number;
    ContactName: string;
    DocumentId: any;
    IsPlaceholder: boolean;
    Name: string;
    Percentage: number;
    IrbId: number;
    IrbName: string;
    RequirementLevel: string;
    RequirementsCount: number;
    DocumentTitle: string;
    DocumentTypeCategoryId: number;
    DocumentTypeId: number;
    LanguageCode: string;
    WorkflowStatus: any; // ???
    FileName?: string;
    DocumentUrl?: string;
    EventId?: number;
    showValue?: boolean;
    showPercent?: boolean;
    Type?: string;
    TypeId?: number;
    SubTypeId?: number;
    TopicId?: number;
    TopicTypeId?: number;
    Color?: string;
    OverdueCount?: number;
    Count?: number;
    IsChildStatus?: boolean;
    IsHiddenStatus?: boolean;
    Group?: string;
  }

  export interface RequiredDocuments {
    TotalRowCount: number;
    RequiredDocuments: RequiredDocument[];
  }

  export interface RequiredDocument {
    Contacts: any[]; //TODO
    CreatedBy: number;
    CreatedDate: string;
    DocumentTypeId: number;
    Languages: any[]; // TODO:
    LastUpdatedBy: number;
    LastUpdatedDate: string;
    EventTypes: EventType[];
    MissingDocumentTypeFields: any[];
    EventTopicId: number;
    PlaceholderTopicId: number;
    Processes: Processes[];
    ProvidedBy: RequiredProvidedLookup;
    ProvidedById: number;
    RequiredBy: RequiredProvidedLookup;
    RequiredById: number;
    RequiredDocumentId: number;
    RequiredDocumentMetadata: any[]; // TODO:
    SiteId: number;
  }

  interface EventType {
    DocumentTypeTopicId: number;
    EventTypeTopicId: number;
  }
}

export namespace PlaceholderServiceOptions {
  export interface GetPlaceholder {
    siteId: number;
    placeholderTopicId: number;
    includeQueryTickets?: boolean;
  }

  export interface GetPlaceholderLink {
    siteId: number;
    placeholderTopicId: number;
  }

  export interface EditPlaceholder {
    siteId: number;
    docPlaceholder: PlaceholderServiceModel.GetPlaceholder;
  }

  export interface CreatePlaceholder {
    siteId: number;
    docPlaceholder: SimpleTopic;
  }

  export interface DeletePlaceholder {
    siteId: number;
    documentPlaceholders: { TopicId: number }[];
  }

  export interface ExportRequirementsStatus {
    requirementLevel: string;
    siteId: number;
    viewMode: number;
  }

  export interface GetPlaceholdersOptions {
    siteId: number;
    requiredBy?: RequiredProvidedLookup;
    requiredById?: string;
    providedBy?: RequiredProvidedLookup;
    providedById?: string;
    start?: number;
    limit?: number;
    orderBy?: string;
  }

  export interface GetPlaceholders {
    DocumentPlaceholders: GetPlaceholdersData[];
    TotalRowCount: number;
  }

  export interface GetPlaceholdersData {
    CreatedBy: number;
    CreatedDate: string;
    LockedBy: any;
    ParentId: any;
    RequiredDocument: RequiredDocumentServiceModel.RequiredDocument;
    SiteId: number;
    TopicFields: TopicField[];
    TopicId: number;
    TopicName: string;
    TopicTypeId: number;
    TopicWorkflowStageFields: any[];
  }

  export interface MatchDocumentToPlaceholdersOptions {
    siteId: number;
    document: {
      TopicId?: number;
      TopicFields?: TopicField[];
    };
    updateDocumentFields?: boolean;
  }

  export interface MatchDocumentToPlaceholdersData {
    ErrorRecords: ErrorRecord[];
    Placeholders: PlaceholderServiceModel.GetPlaceholder[];
    Status: MatchDocumentStatus;
    Document: FolderManagementServiceModel.Document;
  }
}

export namespace PlaceholderServiceModel {
  export interface GetPlaceholder extends Topic {
    RequiredDocument: RequiredDocumentServiceModel.RequiredDocument;
    TopicWorkflowStageFields?: TopicServiceOptions.TopicWorkflowStageData[];
  }
}

export enum ChangeRequiredDocumentStatusMode {
  Default = 0,
  AllSitesForCountry = 1,
}

export enum MatchDocumentStatus {
  NoMatches = 0,
  AutoMatching = 1,
  ManualMatching = 2,
}

@Injectable()
export class RequiredDocumentService extends ServiceBase {
  serviceUrl = 'RequiredDocumentService.svc';

  constructor(
    backend: HttpXhrBackend,
    requestProxy: CASAuthRequestProxy,
    private timeZoneService: TimeZoneService,
  ) {
    super(backend, requestProxy);
  }

  getRequirementsStatus(
    body: RequiredDocumentServiceOptions.GetRequirementsStatus,
    ...params
  ): Observable<OrigamiResponse<RequiredDocumentServiceModel.RequirementsStatus[]>> {
    return this.origamiPost('/GetRequirementsStatus', body, null, null, true);
  }

  getRequiredDocuments(
    body: RequiredDocumentServiceOptions.GetRequiredDocuments,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest = true,
  ): Observable<OrigamiResponse<RequiredDocumentServiceModel.RequiredDocuments>> {
    return this.origamiPost('/GetRequiredDocuments', body, options, silentErrorCodes, forceRequest);
  }

  createOrEditRequiredDocument(
    body: RequiredDocumentServiceOptions.CreateOrEditRequiredDocument,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<void>> {
    return this.origamiPost('/CreateOrEditRequiredDocument', body, null, silentErrorCodes, true);
  }

  getPlaceholder(
    body: PlaceholderServiceOptions.GetPlaceholder,
    force: boolean = true,
  ): Observable<OrigamiResponse<PlaceholderServiceModel.GetPlaceholder>> {
    return this.origamiPost('/GetPlaceholder', body, null, null, force).pipe(
      tap((res) => {
        if (res.Success) {
          const result = res.Result;
          result.QueryTickets?.forEach((queryTicket) => {
            queryTicket.CreatedDateString = this.formatDate(queryTicket.CreatedDate);
            queryTicket.LastUpdatedDateString = this.formatDate(queryTicket.LastUpdatedDate);
            queryTicket.QueryTicketActivities.forEach((activity) => {
              activity.CreatedDateString = this.formatDate(activity.CreatedDate);
            });
          });
        }
      }),
    );
  }

  private formatDate(dateString: string): string {
    if (!dateString) {
      return null;
    }
    const date = Converter.DateAndTime.fromCSharpString(dateString);
    return this.timeZoneService.formatDate(date, DateFormatStore.luxonDateTimeFormatWithTime);
  }

  getPlaceholderLink(
    body: PlaceholderServiceOptions.GetPlaceholderLink,
    ...params
  ): Observable<OrigamiResponse<string>> {
    return this.origamiPost('/GetPlaceholderLink', body, null, null, true);
  }

  createPlaceholder(body: PlaceholderServiceOptions.CreatePlaceholder, ...params): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/CreatePlaceholder', body, null, null, true);
  }

  editPlaceholder(body: PlaceholderServiceOptions.EditPlaceholder, ...params): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/EditPlaceholder', body, null, null, true);
  }

  deletePlaceholders(body: PlaceholderServiceOptions.DeletePlaceholder, ...params): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/DeletePlaceholders', body, null, null, true);
  }

  exportRequirementsStatus(body: PlaceholderServiceOptions.ExportRequirementsStatus): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ExportRequirementsStatus', body, null, null, true);
  }

  deleteRequiredDocument(
    body: RequiredDocumentServiceOptions.DeleteRequiredDocument,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/DeleteRequiredDocument', body, null, silentErrorCodes, true);
  }

  getRequiredDocumentEntities(
    body: RequiredDocumentServiceOptions.GetRequiredDocumentEntities,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<RequiredDocumentServiceOptions.GetRequiredDocumentEntitiesResponse[]>> {
    return this.origamiPost('/GetRequiredDocumentEntities', body, null, silentErrorCodes, true);
  }

  changeRequiredDocumentStatus(
    body: RequiredDocumentServiceOptions.ChangeRequiredDocumentStatus,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/ChangeRequiredDocumentStatus', body, null, silentErrorCodes, true);
  }

  getPlaceholders(
    body: PlaceholderServiceOptions.GetPlaceholdersOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<PlaceholderServiceOptions.GetPlaceholders>> {
    return this.origamiPost('/GetPlaceholders', body, null, silentErrorCodes, true);
  }

  matchDocumentToPlaceholders(
    body: PlaceholderServiceOptions.MatchDocumentToPlaceholdersOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<PlaceholderServiceOptions.MatchDocumentToPlaceholdersData>> {
    return this.origamiPost('/MatchDocumentToPlaceholders', body, null, silentErrorCodes, true);
  }
}
